import React from "react";

import { Box, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IProfileAssetAllocation } from "store/types/user/social";

const GRAPH_COLORS = [
  { main: "#066471", direct: "#187b89", funds: "#298d9b" },
  { main: "#26a6ae", direct: "#35b6be", funds: "#55c9d0" },
  { main: "#96c59b", direct: "#86ae8a", funds: "#90ba94" },
  { main: "#e89960", direct: "#c77f4a", funds: "#d09971" },
  { main: "#e3b76a", direct: "#c19e60", funds: "#d4ac67" },
  { main: "#d46535", direct: "#e27241", funds: "#ee8355" },
  { main: "#b2733e", direct: "#c48551", funds: "#d39968" },
  { main: "#803c20", direct: "#945033", funds: "#a56245" },
  { main: "#7d7d7d", direct: "#949494", funds: "#a8a8a8" }
];

const FALLBACK_COLOR = "#5d7782";

const truncatedText = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.2
} as const;

interface SubCellProps {
  label: string;
  value: number;
  width: string;
  bgcolor: string;
  showText: boolean;
}

const SubCell: React.FC<SubCellProps> = ({ label, value, width, bgcolor, showText }) => (
  <MaterialTooltip title={`${label} ${value}%`} isShow>
    <Box sx={{ width, bgcolor, color: "rgba(255,255,255,0.6)", px: "7px", py: "4px", overflow: "hidden" }}>
      {showText && (
        <>
          <Typography sx={{ ...truncatedText, fontSize: "clamp(8px, calc(0.5vw + 6px), 12px)" }}>{value}%</Typography>
          <Typography sx={{ ...truncatedText, fontSize: "clamp(8px, calc(0.5vw + 6px), 12px)" }}>{label}</Typography>
        </>
      )}
    </Box>
  </MaterialTooltip>
);

interface AllocationCellProps {
  item: IProfileAssetAllocation;
  colorIndex: number;
}

const AllocationCell: React.FC<AllocationCellProps> = ({ item, colorIndex }) => {
  const colors = GRAPH_COLORS[colorIndex] || GRAPH_COLORS[GRAPH_COLORS.length - 1];
  const hasId = !!item.id;

  return (
    <Box sx={{ width: `${item.percentage}%` }}>
      <Stack gap="1px" sx={{ height: "115px" }}>
        <MaterialTooltip title={`${item.title} ${item.percentage}%`} isShow>
          <Box
            sx={{
              overflow: "hidden",
              width: "100%",
              height: item.splittable ? "72px" : "100%",
              color: "#fff",
              bgcolor: hasId ? colors.main : FALLBACK_COLOR,
              display: "flex",
              flexDirection: "column",
              px: "7px",
              py: "4px"
            }}
          >
            <Typography sx={{ ...truncatedText, fontSize: "clamp(12px, calc(0.8vw + 8px), 16px)", fontWeight: 500 }}>
              {item.percentage}%
            </Typography>
            <Typography sx={{ ...truncatedText, fontSize: "clamp(10px, calc(0.7vw + 6px), 16px)" }}>
              {item.title}
            </Typography>
          </Box>
        </MaterialTooltip>

        {item.splittable && (
          <Stack direction="row" sx={{ height: "45px", gap: "1px", overflow: "hidden" }}>
            <SubCell
              label="Direct"
              value={item.direct}
              width={`calc(${item.direct}% - 0.5px)`}
              bgcolor={hasId ? colors.direct : FALLBACK_COLOR}
              showText={hasId}
            />
            <SubCell
              label="Funds"
              value={item.funds}
              width={`calc(${item.funds}% - 0.5px)`}
              bgcolor={hasId ? colors.funds : FALLBACK_COLOR}
              showText={hasId}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

interface IAssetAllocationProps {
  data: IProfileAssetAllocation[];
}

export const AssetAllocation: React.FC<IAssetAllocationProps> = ({ data }) => {
  if (!data.length) return null;

  return (
    <Stack
      direction="row"
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: "8px",
        gap: "1px"
      }}
    >
      {data.map(
        (item, index) => !!item.percentage && <AllocationCell key={item.id ?? index} item={item} colorIndex={index} />
      )}
    </Stack>
  );
};
